import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import * as PropTypes from 'prop-types';
import * as api from '../../../store/apiClient';
import moment from 'moment/moment';
import * as LoginActions from '../../../store/pages/login/actions';
import Loading from '../../global/loading';
import { Product } from '../../../store/pages/products/types';
import ProductStockLevelTable from './productStockLevelTable';
import ProductStockLevelHistoryGraph from './productStockLevelHistoryGraph';
import ApiError from '../../global/apiError';
import { clickHandler } from '../../../utils/util';
import StockProductManualAdjustmentPanel from './stockProductManualAdjustmentPanel';
import { bindActionCreators, Dispatch } from "redux";
import { DateFormat } from "../../../store/pages/venues/types";
import { ProductStockLevel } from '../../../store/pages/productStock/types';
import { GetStockLevelsResponse } from '../../../store/pages/productStock/actions';

interface MappedReduxState {
    selectedVenueId: string;
    dateFormat: DateFormat;
    products: Product[];
    productsLoading: boolean;
}

interface Actions {
    logout: () => void;
}

interface LocalProps {
    productId: string;
    productVariantId: string | null;
    close: () => void;
}

type ProductStockOverviewModalProps = MappedReduxState & Actions & LocalProps;

interface ProductStockOverviewModalState {
    isLoading: boolean;
    loadingError: api.ApiError | null;
    stockLevelsForSelectedProduct: ProductStockLevel | null;
    isManualAdjustmentPanelVisible: boolean;
}

class ProductStockOverviewModal extends React.Component<ProductStockOverviewModalProps, ProductStockOverviewModalState> {
    constructor(props: ProductStockOverviewModalProps) {
        super(props);
        this.state = this.buildStateFromProps(props);
    }

    static contextTypes = {
        t: PropTypes.func
    }

    private buildStateFromProps(props: ProductStockOverviewModalProps): ProductStockOverviewModalState {
        return {
            isLoading: false,
            loadingError: null,
            stockLevelsForSelectedProduct: null,
            isManualAdjustmentPanelVisible: false,
        };
    }

    componentDidMount() {
        this.loadStockLevelsForProduct();
    }

    loadStockLevelsForProduct = () => {
        const { selectedVenueId, productId, productVariantId, logout } = this.props;

        this.setState({ isLoading: true });

        const startDate = new Date().addDays(-2).toApiDateOnlyString();
        const endDate = new Date().addDays(7).toApiDateOnlyString();

        let url = `api/v1/productStock/${selectedVenueId}/productStockLevels/?productId=${productId}&startDate=${startDate}&endDate=${endDate}`;
        if (productVariantId != null) {
            url += `&productVariantId=${productVariantId}`
        }
        api.getWithAuth<GetStockLevelsResponse>(url, logout)
            .subscribe(res => {
                const levels = res.stockLevels.length > 0 ? res.stockLevels.filter(l => (productVariantId ? l.productVariantId === productVariantId : l.productVariantId === null))[0] : null;
                this.setState({ isLoading: false, loadingError: null, stockLevelsForSelectedProduct: levels });
            }, err => this.setState({ isLoading: false, stockLevelsForSelectedProduct: null, loadingError: err }));
    }

    toggleManualAdjustmentPanel = () => this.setState(s => ({ isManualAdjustmentPanelVisible: !s.isManualAdjustmentPanelVisible }));

    onManualAdjustmentPanelClosed = () => {
        this.toggleManualAdjustmentPanel();
        this.loadStockLevelsForProduct();
    }

    render() {
        const { t } = this.context;
        const { dateFormat, productId, productVariantId, productsLoading, products, close } = this.props;
        const { isLoading, stockLevelsForSelectedProduct, loadingError, isManualAdjustmentPanelVisible } = this.state;

        if (productsLoading || isLoading) return <Loading />;
        if (loadingError) return <ApiError error={loadingError} />;

        const selectedProduct = products.find(p => p.id === productId) || null;
        const selectedProductVariant = selectedProduct?.variants.find(pv => pv.id === productVariantId) || null;

        if (!selectedProduct) return null;

        const currentStockToday = stockLevelsForSelectedProduct?.quantities?.find(pq => moment().isSame(pq.dateInLocalTime, 'day'))?.currentStock ?? 0;

        return <>
            <div className='overlay-header'>
                <h1>
                    {selectedProduct.name} {selectedProductVariant && `- ${selectedProductVariant.name}`}
                </h1>
                <div className='overlay-header-actions'>
                    <button className='btn btn-primary' onClick={e => clickHandler(e, this.toggleManualAdjustmentPanel)}>{t('ProductStockOverviewModal:addAdjustment')}</button>
                    <button className='btn btn-basic' onClick={e => clickHandler(e, close)}>{t('Global:close')}</button>
                </div>
               
            </div>
            <div className='overlay-body'>
                {isManualAdjustmentPanelVisible
                    ? <StockProductManualAdjustmentPanel productId={selectedProduct.id} productVariantId={productVariantId} currentStock={currentStockToday} close={this.onManualAdjustmentPanelClosed} />
                    : null}

                {stockLevelsForSelectedProduct != null && (
                    <>
                        <ProductStockLevelHistoryGraph
                            dateFormat={dateFormat}
                            lowStockWarningQuantity={selectedProduct.lowStockWarningQuantity}
                            productStockLevel={stockLevelsForSelectedProduct}
                        />
                        <ProductStockLevelTable
                            dateFormat={dateFormat}
                            productStockLevel={stockLevelsForSelectedProduct}
                            product={selectedProduct!}
                        />
                    </>
                    )}
            </div>
        </>
    }
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        selectedVenueId: state.venues.selectedVenueId,
        dateFormat: state.venues.dateFormat,
        products: state.products.products.filter(p => !p.archived),
        productsLoading: state.products.isLoading,
    }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
    logout: bindActionCreators(LoginActions.actionCreators.logout, dispatch)
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ProductStockOverviewModal);
